import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import App from './App';
import './App.css';
import './styles/mobile.css';
// Desktop-only background decoration. Loaded here for the same reason
// mobile.css is: it is scoped to a class of device, not to a component, and
// two separate page roots share it.
import './styles/desktop.css';
import { registerMessagingSW } from './hooks/usePushNotifications';

// ── MOUNT, OR ADOPT WHAT IS ALREADY THERE ────────────────────────────────────
// The build is prerendered by react-snap (`postbuild` in package.json): every
// route in `reactSnap.include` ships as real HTML with the page already drawn
// into #root. Before this, the deployed site served 5,351 bytes containing zero
// visible text and no <h1> to Googlebot AND to OpenAI's crawler — measured, not
// assumed. Google runs JavaScript eventually; the AI crawlers do not run it at
// all, so to them the site had no content whatsoever.
//
// ⚠️ `reactSnap.concurrency` IS NOT A PERFORMANCE KNOB — IT IS A ROUTE LIMIT.
// react-snap 1.23 is unmaintained (last release 2022) and on Node 22 it writes
// exactly `concurrency` pages and then stops, silently. At the default of 4 it
// produced three pages plus 404.html and dropped /about and /privacy with no
// error; at 1 it produced only the homepage. It is set to 10 for 5 routes.
// ADDING ROUTES TO `include` MEANS RAISING IT — otherwise the new page simply
// will not be written, and nothing will say so. Verify build/<route>/index.html
// exists after a build.
//
// So the root may arrive with children. `createRoot` would throw that markup
// away and rebuild from scratch, which defeats the point and makes the page
// flash; `hydrateRoot` attaches to it instead and only wires up the events.
// The test is the markup itself rather than a build flag, so `npm start` — where
// nothing is prerendered — still takes the plain path.
const container = document.getElementById('root');

const tree = (
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>
);

// ── PRERENDER FOR CRAWLERS, CLIENT-RENDER FOR PEOPLE ─────────────────────────
// This was `hasChildNodes() ? hydrateRoot : createRoot`, which is react-snap's
// documented pattern — and it did not work. React 18 failed to match the saved
// markup and ended up with TWO app trees inside #root: the prerendered one on
// top, with no handlers on it, and the live one beneath. The page looked
// perfect and was completely dead. Picking a language did nothing, because the
// click landed on markup React had never adopted.
//
// react-snap was written for React 16's `ReactDOM.hydrate` and last released in
// 2022; `hydrateRoot` is stricter about mismatches, and this app gives it
// plenty — a language chosen from storage, a theme attribute, an auth state
// that is unknown at capture time. Any of those makes the first client render
// differ from the captured HTML by design.
//
// So the saved markup is discarded and the app mounts fresh. NONE of the SEO
// value is lost: that value comes entirely from crawlers reading the HTML
// WITHOUT running JavaScript, which is exactly what still happens. What is
// given up is hydration's head start on interactivity, which for a bundle this
// size is noise next to a page that does not respond to clicks at all.
if (container.hasChildNodes()) container.replaceChildren();
ReactDOM.createRoot(container).render(tree);

// ONE service worker, two jobs: web push (so a message reaches the lock screen
// with the app closed) and the persistent GLB model cache. They have to be the
// same worker — a scope can only have one, and registering a second at "/"
// would replace the first. firebase-messaging-sw.js importScripts the GLB one.
//
// Registered after `load` so it never delays first paint.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => { registerMessagingSW(); });
}
